/**
 * Live tile preview for the add/edit tile dialog — shows what the tile will
 * look like on the dashboard, driven by the CURRENT form values + the last
 * Preview run's rows. Rendering only: it NEVER executes anything — the data
 * pane's Preview button owns fetching (read-only policy), so before a run the
 * frame shows a "Run Preview" hint. Chart config comes from the same
 * `chartConfigFromValues` mapper `buildTileFromForm` persists (no drift by
 * construction); stat/table/pivot bodies live in AddTilePreviewBodies. Maps
 * need the full lazy spatial pipeline, so they keep a hint instead.
 */
import { Label } from '@/components/ui/label';
import type { QueryResultSet } from '@/types/queryExecution';
import { ChartRenderer } from '../../../charts/components/ChartRenderer';
import { useChartData } from '../../../charts/hooks/useChartData';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import {
  chartConfigFromValues,
  referenceLinesFromValues,
  type AddTileFormValues,
} from './addTileFormModel';
import {
  PivotPreviewBody,
  PreviewHint,
  StatPreviewBody,
  TablePreviewBody,
  type PreviewBodyProps,
} from './AddTilePreviewBodies';
import type { TilePreview } from './useTilePreview';

/**
 * Numeric, not "100%": the preview mounts inside a dialog section that is
 * still settling layout, and ResponsiveContainer's initial percent-height
 * measurement can catch it collapsed (axis-only chart, no marks). The frame
 * is fixed-height anyway, so the chart height is a known constant. Must stay
 * comfortably above the ~140px the cartesian views reserve around the plot
 * (BarChartView: margin top 20 + bottom 60 + angled XAxis height 60) or the
 * plot area collapses and no marks render.
 */
const CHART_PREVIEW_HEIGHT = 250;

function ChartPreviewBody({ values, resultSet }: PreviewBodyProps) {
  // Same column shape StatPreviewBody/TablePreviewBody derive locally — the
  // preview has no store-level `columnInfos`, so `chartConfigFromValues` gets
  // labels straight from this run's rows.
  const config = chartConfigFromValues(values, analyzeColumns(resultSet.columns, resultSet.rows));
  const { chartData, hasData, resolvedConfig } = useChartData(resultSet, config);
  if (!hasData) return <PreviewHint>Pick X and Y columns to chart this data.</PreviewHint>;
  return (
    <div className="w-full px-2 pb-1" data-testid="add-tile-chart-preview">
      <ChartRenderer
        data={chartData}
        config={resolvedConfig ?? config}
        height={CHART_PREVIEW_HEIGHT}
        isAnimationActive={false}
        resizeDebounceMs={150}
        referenceLines={referenceLinesFromValues(values)}
      />
    </div>
  );
}

function PreviewBody({ values, resultSet }: { values: AddTileFormValues; resultSet?: QueryResultSet }) {
  if (values.tileType === 'map') {
    return (
      <PreviewHint>
        The map renders on the dashboard after you add the tile
        {values.mapGeometryColumn ? ` — geometry: ${values.mapGeometryColumn}` : ''}.
      </PreviewHint>
    );
  }
  if (!resultSet) return <PreviewHint>Run Preview to see this tile with your data.</PreviewHint>;
  if (resultSet.rows.length === 0) {
    return <PreviewHint>No rows — the tile will show its empty state.</PreviewHint>;
  }
  if (values.tileType === 'chart') return <ChartPreviewBody values={values} resultSet={resultSet} />;
  if (values.tileType === 'stat') return <StatPreviewBody values={values} resultSet={resultSet} />;
  if (values.tileType === 'pivot') return <PivotPreviewBody values={values} resultSet={resultSet} />;
  return <TablePreviewBody values={values} resultSet={resultSet} />;
}

export function AddTilePreview({
  values,
  preview,
}: {
  values: AddTileFormValues;
  preview: TilePreview;
}) {
  if (!values.tileType || values.tileType === 'note') return null;
  const picked =
    preview.state.status === 'done'
      ? preview.state.result.resultSets[values.resultSetIndex] ?? preview.state.result.resultSets[0]
      : undefined;
  const resultSet: QueryResultSet | undefined = picked
    ? { columns: picked.columns, rows: picked.previewRows as Record<string, unknown>[] }
    : undefined;
  return (
    <div className="flex flex-col gap-1" data-testid="add-tile-tile-preview">
      <Label className="text-xs text-muted-foreground">Tile preview</Label>
      {/* h-72 matches the Visual source pane — the dialog's two tall panels
          share one scale. Tall enough for the chart's reserved axis space. */}
      <div className="flex h-72 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card">
        <div className="shrink-0 truncate border-b border-border px-2.5 py-1.5 text-xs font-medium">
          {values.title.trim() || `New ${values.tileType}`}
        </div>
        <div className="min-h-0 flex-1">
          <PreviewBody values={values} resultSet={resultSet} />
        </div>
      </div>
    </div>
  );
}
